import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { WorkOrderCard } from '../components/work-orders/WorkOrderCard'
import { WorkOrderCompose } from '../components/work-orders/WorkOrderCompose'
import { WorkOrderDetail } from '../components/work-orders/WorkOrderDetail'
import type { WorkOrder, WorkOrderStatus } from '../types/work-orders'
import type { BrainApproval } from '../types/approvals'
import { fetchBrainWorkOrders, approveBrainWorkOrder, rejectBrainWorkOrder, getWSConfig, fetchPendingApprovals, decideBrainApproval } from '../api/brain'
import { WSClient } from '../lib/ws-client'
import { ErrorState } from '../components/ui'
import { usePageTitle } from '../hooks/usePageTitle'

function parseErrorMessage(raw: string): string {
  if (raw.trimStart().startsWith('{')) {
    try {
      const parsed = JSON.parse(raw)
      if (typeof parsed.error === 'string') return parsed.error
      if (typeof parsed.message === 'string') return parsed.message
    } catch {
      // Ignore invalid JSON and fall back to a generic message.
    }
    return 'Failed to load work orders. Please retry.'
  }
  return raw
}

type FilterTab = 'all' | 'open' | WorkOrderStatus

const TABS: { key: FilterTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open Sent' },
  { key: 'pending_approval', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'rejected', label: 'Rejected' },
]

const AUTO_REFRESH_MS = 30_000
const FOUNDER_AGENT_ID = 'founder'
const OPEN_SENT_STATUSES: readonly WorkOrderStatus[] = ['pending_approval', 'approved', 'in_progress']

function isOpenSentWorkOrder(wo: WorkOrder): boolean {
  return wo.from === FOUNDER_AGENT_ID && (OPEN_SENT_STATUSES as readonly string[]).includes(wo.status)
}

export function WorkOrders() {
  usePageTitle('Work Orders')
  const [searchParams, setSearchParams] = useSearchParams()
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([])
  const [activeTab, setActiveTab] = useState<FilterTab>('all')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date())
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrder | null>(null)
  const [showCompose, setShowCompose] = useState(false)
  const [liveConnected, setLiveConnected] = useState(false)
  const [lastLiveEventAt, setLastLiveEventAt] = useState<Date | null>(null)
  const [pendingApprovals, setPendingApprovals] = useState<BrainApproval[]>([])
  const [approvalActionId, setApprovalActionId] = useState<string | null>(null)
  const [approvalError, setApprovalError] = useState<string | null>(null)
  const [isMobileViewport, setIsMobileViewport] = useState(false)
  const selectedWorkOrderId = searchParams.get('workOrder')
  const composeRequested = searchParams.get('compose') === '1'

  const loadWorkOrders = useCallback(async () => {
    setError(null)

    try {
      const data = await fetchBrainWorkOrders()
      setWorkOrders(Array.isArray(data) ? data : [])
      setLastRefreshed(new Date())
    } catch (err) {
      console.error('Work orders load error:', err)
      const raw = err instanceof Error ? err.message : 'Failed to load work orders'
      setError(parseErrorMessage(raw))
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Initial load
  useEffect(() => {
    loadWorkOrders()
  }, [loadWorkOrders])

  useEffect(() => {
    if (!selectedWorkOrderId) {
      setSelectedWorkOrder(null)
      return
    }

    const match = workOrders.find((wo) => wo._doc_id === selectedWorkOrderId || wo.id === selectedWorkOrderId)
    if (match) {
      setSelectedWorkOrder(match)
    }
  }, [selectedWorkOrderId, workOrders])

  useEffect(() => {
    setShowCompose(composeRequested)
  }, [composeRequested])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia('(max-width: 639px)')
    const apply = () => setIsMobileViewport(mediaQuery.matches)
    apply()

    mediaQuery.addEventListener('change', apply)
    return () => mediaQuery.removeEventListener('change', apply)
  }, [])

  const loadPendingApprovals = useCallback(async () => {
    try {
      const data = await fetchPendingApprovals()
      setPendingApprovals(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Pending approvals load error:', err)
      setApprovalError(err instanceof Error ? err.message : 'Failed to load pending approvals')
    }
  }, [])

  useEffect(() => {
    loadPendingApprovals()
  }, [loadPendingApprovals])

  useEffect(() => {
    const interval = setInterval(() => {
      loadPendingApprovals()
    }, AUTO_REFRESH_MS)
    return () => clearInterval(interval)
  }, [loadPendingApprovals])

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(() => {
      loadWorkOrders()
    }, AUTO_REFRESH_MS)
    return () => clearInterval(interval)
  }, [loadWorkOrders])

  // Live stream for work-order updates
  useEffect(() => {
    let disposed = false
    let client: WSClient | null = null
    let unsubOpen: (() => void) | null = null
    let unsubClose: (() => void) | null = null
    let unsubMessage: (() => void) | null = null

    getWSConfig()
      .then(({ wsUrl, token }) => {
        if (disposed) return
        client = new WSClient(wsUrl, token)

        unsubOpen = client.on('open', () => {
          if (disposed) return
          setLiveConnected(true)
          client?.send({ type: 'subscribe_work_orders' })
        })
        unsubClose = client.on('close', () => {
          if (disposed) return
          setLiveConnected(false)
        })
        unsubMessage = client.on('message', (data: Record<string, unknown>) => {
          if (disposed) return
          if (data.type !== 'work_order_update') return
          const workOrder = data.workOrder as WorkOrder | undefined
          if (!workOrder) return

          setWorkOrders((prev) => {
            const matchIndex = prev.findIndex(
              (wo) =>
                (workOrder._doc_id && wo._doc_id === workOrder._doc_id) ||
                wo.id === workOrder.id,
            )
            if (matchIndex === -1) {
              return [workOrder, ...prev]
            }
            const next = [...prev]
            next[matchIndex] = workOrder
            return next
          })
          setLastRefreshed(new Date())
          setLastLiveEventAt(new Date())
        })
        client.connect()
      })
      .catch((err) => {
        if (disposed) return
        console.error('[work-orders] WS config failed:', err)
      })

    return () => {
      disposed = true
      try {
        client?.send({ type: 'unsubscribe_work_orders' })
      } catch {
        // Best-effort unsubscribe during teardown.
      }
      unsubOpen?.()
      unsubClose?.()
      unsubMessage?.()
      client?.disconnect()
      setLiveConnected(false)
    }
  }, [])

  const handleApprove = async (id: string, notes?: string) => {
    await approveBrainWorkOrder(id, notes)
    // Reload to get updated state
    await loadWorkOrders()
  }

  const handleReject = async (id: string, notes?: string, reason?: string) => {
    await rejectBrainWorkOrder(id, reason || notes)
    await loadWorkOrders()
  }

  const handleApprovalDecision = async (approvalDocId: string, status: 'approved' | 'rejected') => {
    setApprovalActionId(approvalDocId)
    setApprovalError(null)
    try {
      await decideBrainApproval(approvalDocId, status)
      await Promise.all([loadPendingApprovals(), loadWorkOrders()])
    } catch (err) {
      setApprovalError(err instanceof Error ? err.message : 'Approval action failed')
    } finally {
      setApprovalActionId(null)
    }
  }

  const updateQuery = useCallback((next: { workOrder?: string | null; compose?: boolean }) => {
    const params = new URLSearchParams(searchParams)

    if (next.workOrder === null) {
      params.delete('workOrder')
    } else if (typeof next.workOrder === 'string') {
      params.set('workOrder', next.workOrder)
    }

    if (next.compose === false) {
      params.delete('compose')
    } else if (next.compose === true) {
      params.set('compose', '1')
    }

    setSearchParams(params, { replace: true })
  }, [searchParams, setSearchParams])

  // Compute counts per tab
  const counts: Record<FilterTab, number> = {
    all: workOrders.length,
    open: 0,
    pending_approval: 0,
    approved: 0,
    in_progress: 0,
    completed: 0,
    rejected: 0,
    failed: 0,
  }
  for (const wo of workOrders) {
    if (isOpenSentWorkOrder(wo)) {
      counts.open++
    }
    if (wo.status in counts) {
      counts[wo.status as FilterTab]++
    }
  }

  // Filter work orders by active tab
  const filtered =
    activeTab === 'all'
      ? workOrders
      : activeTab === 'open'
        ? workOrders.filter(isOpenSentWorkOrder)
      : workOrders.filter((wo) => wo.status === activeTab)

  // Sort: pending first, then by received_at descending
  const sorted = [...filtered].sort((a, b) => {
    if (a.status === 'pending_approval' && b.status !== 'pending_approval') return -1
    if (b.status === 'pending_approval' && a.status !== 'pending_approval') return 1
    return new Date(b.received_at).getTime() - new Date(a.received_at).getTime()
  })

  const brainOptions = useMemo(() => {
    const values = new Set<string>(['decisive'])
    for (const wo of workOrders) {
      if (wo.to?.trim()) values.add(wo.to.trim())
      if (wo.from?.trim()) values.add(wo.from.trim())
    }
    return Array.from(values)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({
        value,
        label:
          value === 'decisive'
            ? 'Decisive'
            : value === 'mech-browse.gm'
              ? 'ThinkBrowse'
              : value,
      }))
  }, [workOrders])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-swiss-body text-swiss-gray-500">Loading work orders...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-[24px] font-semibold tracking-tight leading-[1.2]">Work Orders</h1>
          <p className="text-swiss-gray-500 mt-2 text-[14px]">Review and approve agent work orders</p>
        </div>
        <ErrorState
          title="Work Orders Error"
          message={error}
          onRetry={loadWorkOrders}
        />
      </div>
    )
  }

  // Detail view
  if (selectedWorkOrder) {
    return (
      <div>
        <WorkOrderDetail
          workOrder={selectedWorkOrder}
          onBack={() => {
            setSelectedWorkOrder(null)
            updateQuery({ workOrder: null })
          }}
          onUpdated={() => {
            setSelectedWorkOrder(null)
            updateQuery({ workOrder: null })
            loadWorkOrders()
          }}
        />
      </div>
    )
  }

  if (showCompose) {
    return (
      <WorkOrderCompose
        mobileTakeover={isMobileViewport}
        onClose={() => {
          setShowCompose(false)
          updateQuery({ compose: false })
        }}
        brainOptions={brainOptions}
        onCreated={() => {
          setShowCompose(false)
          updateQuery({ compose: false })
          loadWorkOrders()
        }}
      />
    )
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-[24px] font-semibold tracking-tight leading-[1.2]">
            Work Orders
          </h1>
          <p className="text-swiss-gray-500 mt-2 text-[14px]">
            Review and approve agent work orders
          </p>
        </div>

        {/* Refresh button */}
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <span className="min-w-0 text-swiss-caption font-swiss-mono text-swiss-gray-400">
            Live {liveConnected ? 'ON' : 'OFF'}
            {lastLiveEventAt ? ` · ${lastLiveEventAt.toLocaleTimeString()}` : ''}
          </span>
          <span className="min-w-0 text-swiss-caption font-swiss-mono text-swiss-gray-400">
            {lastRefreshed.toLocaleTimeString()}
          </span>
          <button
            onClick={loadWorkOrders}
            className="px-3 py-1.5 text-[13px] border border-swiss-gray-200 text-swiss-black bg-transparent cursor-pointer transition-colors duration-150 hover:border-swiss-black"
            aria-label="Refresh work orders"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Status Filter Tabs */}
      {pendingApprovals.length > 0 && (
        <section className="mb-6 border border-swiss-black bg-swiss-off-white px-4 py-4 sm:px-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-widest text-swiss-gray-500">Pending tool approvals</p>
              <h2 className="mt-1 text-[18px] font-semibold tracking-tight">
                {pendingApprovals.length} action{pendingApprovals.length === 1 ? '' : 's'} waiting on you
              </h2>
            </div>
          </div>
          {approvalError && (
            <p className="mt-3 text-[13px] text-swiss-red">{approvalError}</p>
          )}
          <div className="mt-4 space-y-3">
            {pendingApprovals.map((approval) => {
              const actionPending = approvalActionId === approval._doc_id
              const inputSummary = typeof approval.input?.command === 'string'
                ? approval.input.command
                : JSON.stringify(approval.input)

              return (
                <div key={approval._doc_id} className="border border-swiss-gray-200 bg-swiss-white px-4 py-4">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-widest text-swiss-gray-500">
                        <span>{approval.tier}</span>
                        <span>{approval.brainId || 'unknown brain'}</span>
                        <span>{approval.tool}</span>
                      </div>
                      <h3 className="mt-2 text-[15px] font-semibold tracking-tight text-swiss-black">
                        Approval required for {approval.tool}
                      </h3>
                      <p className="mt-2 max-w-[80ch] whitespace-pre-wrap text-[13px] leading-6 text-swiss-gray-600">
                        {approval.reason || 'No approval reason provided.'}
                      </p>
                      <p className="mt-2 max-w-[80ch] whitespace-pre-wrap break-all text-[12px] leading-5 text-swiss-gray-500">
                        {inputSummary}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <button
                        type="button"
                        onClick={() => void handleApprovalDecision(approval._doc_id, 'approved')}
                        disabled={actionPending}
                        className="px-4 py-2 bg-swiss-black text-swiss-white text-[13px] font-medium disabled:opacity-50"
                      >
                        {actionPending ? 'Working…' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleApprovalDecision(approval._doc_id, 'rejected')}
                        disabled={actionPending}
                        className="px-4 py-2 border border-swiss-gray-200 text-[13px] font-medium text-swiss-black disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      <div className="mb-6 overflow-x-auto border-b border-swiss-gray-200" role="tablist" aria-label="Filter work orders by status">
        <div className="flex min-w-max gap-0">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key
          const count = counts[tab.key]
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={isActive}
              aria-controls="work-orders-list"
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-[13px] font-medium cursor-pointer transition-colors duration-150 border-b-2 -mb-px ${
                isActive
                  ? 'border-swiss-black text-swiss-black'
                  : 'border-transparent text-swiss-gray-500 hover:text-swiss-black'
              }`}
            >
              {tab.label}
              {count > 0 && (
                <span
                  className={`ml-2 inline-block min-w-[18px] text-center px-1 py-0 text-swiss-caption font-medium ${
                    isActive
                      ? 'bg-swiss-black text-swiss-white'
                      : 'bg-swiss-gray-200 text-swiss-gray-600'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          )
        })}
        </div>
      </div>

      {/* Work Orders List */}
      <div id="work-orders-list" role="tabpanel" aria-label={`${activeTab === 'all' ? 'All' : activeTab} work orders`}>
        {sorted.length === 0 ? (
          <div className="bg-swiss-off-white border border-swiss-gray-200 p-6 text-center sm:p-12">
            <div className="text-swiss-body text-swiss-gray-500 mb-2">
              No work orders
            </div>
            <div className="text-swiss-small text-swiss-gray-400">
              {activeTab === 'all'
                ? 'No work orders have been received yet'
                : `No work orders with status "${activeTab.replace('_', ' ')}"`}
            </div>
          </div>
        ) : (
          <div>
            {sorted.map((wo) => (
              <WorkOrderCard
                key={wo.id}
                workOrder={wo}
                onApprove={handleApprove}
                onReject={handleReject}
                onClick={(workOrder) => {
                  setSelectedWorkOrder(workOrder)
                  updateQuery({ workOrder: workOrder._doc_id || workOrder.id })
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Compose FAB */}
      <button
        onClick={() => {
          setShowCompose(true)
          updateQuery({ compose: true })
        }}
        className="fixed bottom-4 right-4 z-10 flex h-12 w-12 items-center justify-center bg-swiss-black text-swiss-h1 text-swiss-white transition-colors duration-150 hover:bg-swiss-gray-700 sm:bottom-6 sm:right-6"
        aria-label="Create new work order"
      >
        +
      </button>
    </div>
  )
}
